<template>
  <div class="column-mapping-step">
    <!-- Loading State -->
    <div
      v-if="loading"
      class="loading-container"
    >
      <i class="fas fa-spinner fa-spin fa-2x" />
      <p>{{ t('import.mapping.loading') }}</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="mapping-header">
        <h2 class="mapping-title">
          {{ t('import.mapping.title') }}
        </h2>
        <p class="mapping-subtitle">
          {{ t('import.mapping.subtitle') }}
        </p>
      </div>

      <!-- Validation Message -->
      <SpmMessage
        v-if="validationMessage"
        :severity="isValid ? 'success' : 'warning'"
        class="mb-3"
      >
        {{ validationMessage }}
      </SpmMessage>

      <!-- Mapping Table -->
      <div class="mapping-table-container">
        <table class="mapping-table">
          <thead>
            <tr>
              <th class="col-checkbox">
                <div class="th-content">
                  <span class="column-count-badge">
                    {{ selectedColumnsCount }}/{{ sourceHeaders.length }}
                  </span>
                </div>
              </th>
              <th class="col-source">
                <div class="th-content">
                  <i class="fas fa-file-csv" />
                  {{ t('import.mapping.sourceColumn') }}
                </div>
              </th>
              <th class="col-arrow" />
              <th class="col-target">
                <div class="th-content">
                  <i class="fas fa-database" />
                  {{ t('import.mapping.targetField') }}
                </div>
              </th>
              <th class="col-preview">
                <div class="th-content">
                  <i class="fas fa-eye" />
                  {{ t('import.mapping.preview') }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="header in sourceHeaders"
              :key="header"
              class="mapping-row"
              :class="{
                'is-mapped': localMapping[header],
                'is-excluded': !selectedColumns[header]
              }"
            >
              <!-- Checkbox -->
              <td class="col-checkbox">
                <Checkbox
                  v-model="selectedColumns[header]"
                  :binary="true"
                  :input-id="`col-${header}`"
                  @change="onColumnSelectionChange(header)"
                />
              </td>

              <!-- Source Column -->
              <td class="col-source">
                <div class="source-cell">
                  <span class="source-name">{{ header }}</span>
                </div>
              </td>

              <!-- Arrow -->
              <td class="col-arrow">
                <i
                  class="fas fa-arrow-right arrow-icon"
                  :class="{ 'is-active': localMapping[header] && selectedColumns[header] }"
                />
              </td>

              <!-- Target Field Dropdown -->
              <td class="col-target">
                <div class="target-field-container">
                  <!-- Grouped dropdown for contacts -->
                  <Dropdown
                    v-if="importType === 'contact'"
                    v-model="localMapping[header]"
                    :options="getAvailableTargetFields(header)"
                    option-label="label"
                    option-value="value"
                    option-group-label="label"
                    option-group-children="items"
                    :placeholder="t('import.mapping.selectField')"
                    class="target-dropdown"
                    :class="{ 'is-disabled': !selectedColumns[header] }"
                    show-clear
                    :filter="targetFields.length > 10"
                    :disabled="!selectedColumns[header]"
                  >
                    <template #optiongroup="{ option }">
                      <div class="option-group-header">
                        <span class="option-group-label">{{ option.label }}</span>
                      </div>
                    </template>
                    <template #option="{ option }">
                      <div class="target-option">
                        <!-- Email icon -->
                        <span
                          v-if="option.value === 'email'"
                          class="field-icon email-icon"
                        >
                          <i class="fas fa-envelope" />
                        </span>
                        <!-- Phone icon -->
                        <span
                          v-else-if="option.value === 'phone_number'"
                          class="field-icon phone-icon"
                        >
                          <i class="fas fa-mobile-alt" />
                        </span>
                        <!-- Push notification icon -->
                        <span
                          v-else-if="isPushField(option.value)"
                          class="field-icon push-icon"
                        >
                          <i class="fas fa-bell" />
                        </span>
                        <!-- Consent icon -->
                        <span
                          v-else-if="option.value.includes('consent')"
                          class="field-icon consent-icon"
                        >
                          <i class="fas fa-check-circle" />
                        </span>
                        <span class="option-label">{{ option.label }}</span>
                        <i
                          v-if="option.required"
                          class="fas fa-asterisk required-icon"
                          :title="t('import.mapping.requiredField')"
                        />
                      </div>
                    </template>
                    <template #value="{ value }">
                      <div
                        v-if="value"
                        class="selected-value"
                      >
                        <span>{{ getFieldLabel(value) }}</span>
                        <i
                          v-if="isFieldRequired(value)"
                          class="fas fa-asterisk required-icon"
                          :title="t('import.mapping.requiredField')"
                        />
                      </div>
                      <span
                        v-else
                        class="placeholder-text"
                      >
                        {{ t('import.mapping.selectField') }}
                      </span>
                    </template>
                  </Dropdown>

                  <!-- Simple dropdown for custom_data (no groups) -->
                  <Dropdown
                    v-else
                    v-model="localMapping[header]"
                    :options="getAvailableFlatTargetFields(header)"
                    option-label="label"
                    option-value="value"
                    :placeholder="t('import.mapping.selectField')"
                    class="target-dropdown"
                    :class="{ 'is-disabled': !selectedColumns[header] }"
                    show-clear
                    filter
                    :filter-placeholder="t('import.mapping.searchField')"
                    :disabled="!selectedColumns[header]"
                  >
                    <template #option="{ option }">
                      <div class="target-option">
                        <span class="option-label">{{ option.label }}</span>
                        <i
                          v-if="option.required"
                          class="fas fa-asterisk required-icon"
                          :title="t('import.mapping.requiredField')"
                        />
                      </div>
                    </template>
                    <template #value="{ value }">
                      <div
                        v-if="value"
                        class="selected-value"
                      >
                        <span>{{ getFieldLabel(value) }}</span>
                        <i
                          v-if="isFieldRequired(value)"
                          class="fas fa-asterisk required-icon"
                          :title="t('import.mapping.requiredField')"
                        />
                      </div>
                      <span
                        v-else
                        class="placeholder-text"
                      >
                        {{ t('import.mapping.selectField') }}
                      </span>
                    </template>
                  </Dropdown>
                  <!-- Format hint displayed below dropdown when a field is selected -->
                  <div
                    v-if="localMapping[header] && getFieldFormatHint(localMapping[header])"
                    class="field-format-hint"
                  >
                    <i class="fas fa-info-circle" />
                    <span>{{ getFieldFormatHint(localMapping[header]) }}</span>
                  </div>
                </div>
              </td>

              <!-- Preview Value -->
              <td class="col-preview">
                <span
                  v-if="firstDataRow[header]"
                  class="preview-value"
                >
                  {{ truncateValue(firstDataRow[header]) }}
                </span>
                <span
                  v-else
                  class="preview-empty"
                >
                  {{ t('import.mapping.emptyValue') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- VAPID Key Selector (shown when all 3 push fields are mapped) -->
      <div
        v-if="pushMappingStatus.isComplete && importType === 'contact'"
        class="vapid-key-selector"
      >
        <div class="vapid-key-selector__header">
          <i class="fas fa-key" />
          <span>{{ t('import.mapping.vapidKey.title') }}</span>
        </div>
        <div class="vapid-key-selector__body">
          <p class="vapid-key-selector__hint">
            {{ t('import.mapping.vapidKey.hint') }}
          </p>
          <Dropdown
            v-model="selectedVapidKeyId"
            :options="vapidKeyOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('import.mapping.vapidKey.placeholder')"
            class="vapid-key-selector__dropdown"
            :loading="loadingVapidKeys"
          >
            <template #option="{ option }">
              <div class="vapid-key-option">
                <i class="fas fa-key vapid-key-option__icon" />
                <div class="vapid-key-option__content">
                  <span class="vapid-key-option__label">{{ option.label }}</span>
                  <span
                    v-if="option.isDefault"
                    class="vapid-key-option__badge"
                  >{{ t('import.mapping.vapidKey.default') }}</span>
                </div>
              </div>
            </template>
          </Dropdown>
          <small
            v-if="pushMappingStatus.isComplete && !selectedVapidKeyId && !loadingVapidKeys"
            class="p-error"
          >
            {{ t('import.mapping.vapidKey.required') }}
          </small>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, onMounted, PropType,
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import { useI18n } from 'vue-i18n';
import type {
  ImportAdapter, ImportContext, FieldDefinition, ImportFieldMapping,
} from '@/composables/import/types';
import { showToastError } from '@/helpers';
import { nestCall, NestApiType } from '@/composables/nestApi';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';

interface TargetFieldOption {
  label: string;
  value: string;
  required: boolean;
  isIdentifier: boolean;
  group?: string;
}

interface VapidKeyOption {
  label: string;
  value: string;
  isDefault: boolean;
}

export default defineComponent({
  name: 'ColumnMappingStep',

  components: {
    Dropdown,
    Checkbox,
    SpmMessage,
  },

  props: {
    /** Source column headers from the file */
    sourceHeaders: {
      type: Array as PropType<string[]>,
      required: true,
    },

    /** First row of data for preview */
    firstDataRow: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    /** Import adapter */
    adapter: {
      type: Object as PropType<ImportAdapter>,
      required: true,
    },

    /** Import type (contact, custom_data, etc.) */
    importType: {
      type: String,
      required: true,
    },

    /** Additional params for the adapter */
    params: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    /** Shop ID for fetching VAPID keys */
    shopId: {
      type: [Number, String] as PropType<number | string>,
      default: null,
    },

    /** Current mapping (v-model) */
    modelValue: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'mapping-change', 'valid-change', 'vapid-key-change'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const loading = ref(true);
    const targetFields = ref<FieldDefinition[]>([]);

    // Local mapping: sourceColumn -> targetField
    const localMapping = ref<Record<string, string>>({});

    // VAPID key state
    const selectedVapidKeyId = ref<string | null>(null);
    const vapidKeyOptions = ref<VapidKeyOption[]>([]);
    const loadingVapidKeys = ref(false);

    // Selected columns for import (checkbox state)
    const selectedColumns = ref<Record<string, boolean>>({});

    // ==========================================================================
    // COMPUTED PROPERTIES
    // ==========================================================================

    /**
     * Count of selected columns
     */
    const selectedColumnsCount = computed(() => Object.values(selectedColumns.value).filter(Boolean).length);

    /**
     * Convert target fields to dropdown options
     * For contacts: translate labels via i18n
     * For custom_data: use labels directly from schema
     */
    const targetFieldOptions = computed((): TargetFieldOption[] => targetFields.value.map((field) => {
      let label = field.name;
      if (field.label) {
        // For contacts, label is a translation key
        // For custom_data, label is the actual label from schema
        label = props.importType === 'contact' ? t(field.label) : field.label;
      }
      return {
        label,
        value: field.name,
        required: field.required,
        isIdentifier: field.isIdentifier || false,
        group: field.group,
      };
    }));

    /**
     * Interface for grouped dropdown options
     */
    interface GroupedOptions {
      label: string;
      items: TargetFieldOption[];
    }

    /**
     * Get already mapped target fields (to exclude from other dropdowns)
     */
    const mappedTargetFields = computed(() => {
      const mapped = new Set<string>();
      Object.values(localMapping.value).forEach((target) => {
        if (target) mapped.add(target);
      });
      return mapped;
    });

    /**
     * Get identifier fields
     */
    const identifierFields = computed(() => targetFields.value.filter((f) => f.isIdentifier));

    /**
     * Get required fields (non-identifier)
     */
    const requiredFields = computed(() => targetFields.value.filter((f) => f.required && !f.isIdentifier));

    /**
     * Push notification fields
     */
    const PUSH_FIELDS = ['push_endpoint', 'push_key', 'push_token'];

    /**
     * Check push notification mapping status
     */
    const pushMappingStatus = computed(() => {
      const mappedTargets = new Set(Object.values(localMapping.value).filter(Boolean));
      const mappedPushFields = PUSH_FIELDS.filter((f) => mappedTargets.has(f));

      return {
        count: mappedPushFields.length,
        isComplete: mappedPushFields.length === 3,
        isPartial: mappedPushFields.length > 0 && mappedPushFields.length < 3,
        isEmpty: mappedPushFields.length === 0,
        mappedFields: mappedPushFields,
        missingFields: PUSH_FIELDS.filter((f) => !mappedTargets.has(f)),
      };
    });

    /**
     * Check if a field is a push notification field
     */
    const isPushField = (fieldName: string): boolean => PUSH_FIELDS.includes(fieldName);

    /**
     * Check if at least one identifier is mapped
     */
    const hasIdentifierMapped = computed(() => {
      const mappedTargets = new Set(Object.values(localMapping.value).filter(Boolean));

      // For contacts: email OR phone_number OR (push_endpoint AND push_key AND push_token)
      if (props.importType === 'contact') {
        if (mappedTargets.has('email') || mappedTargets.has('phone_number')) {
          return true;
        }
        // Check push notification (all 3 required)
        if (pushMappingStatus.value.isComplete) {
          return true;
        }
        return false;
      }

      // For other types: any identifier is enough
      return identifierFields.value.some((f) => mappedTargets.has(f.name));
    });

    /**
     * Check if all required fields are mapped
     */
    const allRequiredFieldsMapped = computed(() => requiredFields.value.every((field) => {
      const mappedTargets = new Set(Object.values(localMapping.value).filter(Boolean));
      return mappedTargets.has(field.name);
    }));

    /**
     * Check if all selected columns have a mapping
     */
    const allSelectedColumnsHaveMapping = computed(() => props.sourceHeaders.every((header) => {
      // If column is not selected, it doesn't need a mapping
      if (!selectedColumns.value[header]) return true;
      // If column is selected, it must have a mapping
      return !!localMapping.value[header];
    }));

    /**
     * Get selected columns without mapping (for warning)
     */
    const selectedColumnsWithoutMapping = computed(() => props.sourceHeaders.filter(
      (header) => selectedColumns.value[header] && !localMapping.value[header],
    ));

    /**
     * Check if mapping is valid
     */
    const isValid = computed(() => {
      // Must have at least one mapping
      const hasMappings = Object.values(localMapping.value).some(Boolean);
      if (!hasMappings) return false;

      // All selected columns must have a mapping
      if (!allSelectedColumnsHaveMapping.value) return false;

      // Push notification must be complete (all 3) or empty (none)
      if (pushMappingStatus.value.isPartial) return false;

      // If push fields are complete, a VAPID key must be selected
      if (pushMappingStatus.value.isComplete && !selectedVapidKeyId.value) return false;

      // For contacts: need identifier
      if (props.importType === 'contact') {
        return hasIdentifierMapped.value;
      }

      // For custom data: need all required fields
      return allRequiredFieldsMapped.value;
    });

    /**
     * Validation message to display
     */
    const validationMessage = computed(() => {
      if (isValid.value) {
        return t('import.mapping.validMapping');
      }

      // Check if selected columns are missing mapping
      if (!allSelectedColumnsHaveMapping.value) {
        const count = selectedColumnsWithoutMapping.value.length;
        return t('import.mapping.selectedColumnsNeedMapping', { count });
      }

      // Check for partial push notification mapping
      if (pushMappingStatus.value.isPartial) {
        return t('import.mapping.pushIncomplete');
      }

      if (props.importType === 'contact' && !hasIdentifierMapped.value) {
        return t('import.mapping.needIdentifier');
      }

      if (!allRequiredFieldsMapped.value) {
        return t('import.mapping.needRequiredFields');
      }

      return t('import.mapping.needMapping');
    });

    // ==========================================================================
    // METHODS
    // ==========================================================================

    /**
     * Get available target fields for a source column (grouped)
     * Excludes already mapped fields (except the current one)
     */
    const getAvailableTargetFields = (sourceColumn: string): GroupedOptions[] => {
      const currentTarget = localMapping.value[sourceColumn];

      // Filter available options
      const availableOptions = targetFieldOptions.value.filter(
        (option) => !mappedTargetFields.value.has(option.value) || option.value === currentTarget,
      );

      // Group them
      const groups: Record<string, TargetFieldOption[]> = {};
      availableOptions.forEach((option) => {
        const groupKey = option.group || 'import.contact.groups.additional';
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(option);
      });

      // Define group order
      const groupOrder = [
        'import.contact.groups.identification',
        'import.contact.groups.consent',
        'import.contact.groups.additional',
      ];

      return groupOrder
        .filter((key) => groups[key]?.length > 0)
        .map((key) => ({
          label: t(key),
          items: groups[key],
        }));
    };

    /**
     * Get available target fields as flat list (for custom_data - no groups)
     * Excludes already mapped fields (except the current one)
     */
    const getAvailableFlatTargetFields = (sourceColumn: string): TargetFieldOption[] => {
      const currentTarget = localMapping.value[sourceColumn];

      return targetFieldOptions.value.filter(
        (option) => !mappedTargetFields.value.has(option.value) || option.value === currentTarget,
      );
    };

    /**
     * Get field label by name
     * For contacts: uses i18n translation
     * For custom_data: uses the label directly from schema
     */
    const getFieldLabel = (fieldName: string): string => {
      const field = targetFields.value.find((f) => f.name === fieldName);
      if (field?.label) {
        // For contacts, label is a translation key
        // For custom_data, label is the actual label from schema
        if (props.importType === 'contact') {
          return t(field.label);
        }
        return field.label;
      }
      return fieldName;
    };

    /**
     * Check if a field is required
     */
    const isFieldRequired = (fieldName: string): boolean => {
      const field = targetFields.value.find((f) => f.name === fieldName);
      return field?.required || false;
    };

    /**
     * Get format hint for a field (if any)
     */
    const getFieldFormatHint = (fieldName: string): string | null => {
      const field = targetFields.value.find((f) => f.name === fieldName);
      if (field?.formatHint) {
        return t(field.formatHint);
      }
      return null;
    };

    /**
     * Truncate preview value
     */
    const truncateValue = (value: unknown): string => {
      const str = String(value);
      return str.length > 30 ? `${str.substring(0, 30)}...` : str;
    };

    /**
     * Auto-map columns based on strict name matching (only high confidence matches)
     * Only runs for contact import type
     */
    const performInitialAutoMapping = () => {
      if (props.importType !== 'contact') return;

      const newMapping: Record<string, string> = {};

      // Define strict mappings: column name variations -> property name
      const strictMappings: Record<string, string> = {
        // Email
        email: 'email',
        mail: 'email',
        emailaddress: 'email',
        adresseemail: 'email',
        adressemail: 'email',
        courriel: 'email',
        // Phone
        phone: 'phone_number',
        phonenumber: 'phone_number',
        telephone: 'phone_number',
        tel: 'phone_number',
        mobile: 'phone_number',
        numerotelephone: 'phone_number',
        numerotel: 'phone_number',
        // First name
        firstname: 'first_name',
        prenom: 'first_name',
        givenname: 'first_name',
        // Last name
        lastname: 'last_name',
        nom: 'last_name',
        familyname: 'last_name',
        surname: 'last_name',
        // Birth date
        birthdate: 'birth_date',
        datedenaissance: 'birth_date',
        datenaissance: 'birth_date',
        birthday: 'birth_date',
        // Language
        language: 'language',
        langue: 'language',
        lang: 'language',
        // City
        city: 'city',
        ville: 'city',
        // Country
        country: 'country',
        pays: 'country',
        // Postal code
        postalcode: 'postal_code',
        zipcode: 'postal_code',
        codepostal: 'postal_code',
        zip: 'postal_code',
        // Company
        company: 'company',
        entreprise: 'company',
        societe: 'company',
        // Address
        address: 'address_line1',
        adresse: 'address_line1',
        addressline1: 'address_line1',
        adresseligne1: 'address_line1',
        address1: 'address_line1',
        adresse1: 'address_line1',
        addressline2: 'address_line2',
        adresseligne2: 'address_line2',
        address2: 'address_line2',
        adresse2: 'address_line2',
        // Region
        region: 'region',
        state: 'region',
        departement: 'region',
        province: 'region',
        // Consents
        emailconsent: 'email_consent',
        consentementemail: 'email_consent',
        smsconsent: 'sms_consent',
        conssentementsms: 'sms_consent',
        whatsappconsent: 'whatsapp_consent',
        consentementwhatsapp: 'whatsapp_consent',
        // Push
        pushendpoint: 'push_endpoint',
        pushkey: 'push_key',
        pushtoken: 'push_token',
      };

      props.sourceHeaders.forEach((header) => {
        // Normalize: lowercase, remove spaces, underscores, dashes
        const headerNormalized = header.toLowerCase().replace(/[_\s-]/g, '');

        // Check for strict match
        const targetProperty = strictMappings[headerNormalized];

        if (targetProperty && !Object.values(newMapping).includes(targetProperty)) {
          newMapping[header] = targetProperty;
        }
      });

      localMapping.value = newMapping;
    };

    /**
     * Handle column selection change
     */
    const onColumnSelectionChange = (header: string) => {
      // If column is deselected, clear its mapping
      if (!selectedColumns.value[header]) {
        delete localMapping.value[header];
      }
    };

    /**
     * Initialize selected columns (all selected by default)
     */
    const initializeSelectedColumns = () => {
      props.sourceHeaders.forEach((header) => {
        if (selectedColumns.value[header] === undefined) {
          selectedColumns.value[header] = true;
        }
      });
    };

    /**
     * Fetch VAPID keys from the API
     */
    const fetchVapidKeys = async () => {
      if (!props.shopId) return;
      loadingVapidKeys.value = true;
      try {
        const response = await nestCall('get', NestApiType.DEFAULT, `/shop/vapid-keys/${props.shopId}`);
        if (response?.success && Array.isArray(response.data)) {
          vapidKeyOptions.value = response.data.map((k: any) => ({
            label: k.label || t('import.mapping.vapidKey.unnamed'),
            value: k.id,
            isDefault: k.is_default,
          }));
          // Auto-select the default key
          const defaultKey = vapidKeyOptions.value.find((k) => k.isDefault);
          if (defaultKey && !selectedVapidKeyId.value) {
            selectedVapidKeyId.value = defaultKey.value;
          }
        }
      } catch (err) {
        console.error('Error fetching VAPID keys:', err);
      } finally {
        loadingVapidKeys.value = false;
      }
    };

    /**
     * Fetch target fields from adapter
     */
    const fetchTargetFields = async () => {
      loading.value = true;

      try {
        const ctx: ImportContext = {
          importType: props.importType,
          params: props.params,
        };

        if (props.adapter.getFields) {
          targetFields.value = await Promise.resolve(props.adapter.getFields(ctx));
        }
      } catch (err) {
        console.error('Error fetching target fields:', err);
        await showToastError(t('import.mapping.fetchError'));
      } finally {
        loading.value = false;
      }
    };

    /**
     * Build mapping DTO for parent
     */
    const buildMappingDto = (): ImportFieldMapping[] => {
      const mappingDto: ImportFieldMapping[] = [];

      Object.entries(localMapping.value).forEach(([source, target]) => {
        // Only include selected columns
        if (target && selectedColumns.value[source]) {
          const field = targetFields.value.find((f) => f.name === target);
          // Get the display label for the target field
          const targetLabel = getFieldLabel(target);
          mappingDto.push({
            source,
            target,
            required: field?.required || false,
            targetLabel,
          });
        }
      });

      return mappingDto;
    };

    // ==========================================================================
    // WATCHERS
    // ==========================================================================

    // Sync with parent modelValue
    watch(() => props.modelValue, (newVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(localMapping.value)) {
        localMapping.value = { ...newVal };
      }
    }, { deep: true });

    // Emit changes to parent when mapping changes
    watch(localMapping, (newVal) => {
      emit('update:modelValue', { ...newVal });
      emit('mapping-change', buildMappingDto());
      emit('valid-change', isValid.value);
    }, { deep: true });

    // Emit changes when selected columns change
    watch(selectedColumns, () => {
      emit('mapping-change', buildMappingDto());
      emit('valid-change', isValid.value);
    }, { deep: true });

    // Emit validity changes
    watch(isValid, (newVal) => {
      emit('valid-change', newVal);
    });

    // Watch push mapping status to fetch VAPID keys when all 3 push fields are mapped
    watch(() => pushMappingStatus.value.isComplete, (isComplete) => {
      if (isComplete && vapidKeyOptions.value.length === 0) {
        fetchVapidKeys();
      }
      if (!isComplete) {
        selectedVapidKeyId.value = null;
      }
    });

    // Emit VAPID key change
    watch(selectedVapidKeyId, (newVal) => {
      const selectedOption = vapidKeyOptions.value.find((k) => k.value === newVal);
      emit('vapid-key-change', {
        id: newVal,
        label: selectedOption?.label || null,
      });
      // Re-emit validity when VAPID key changes
      emit('valid-change', isValid.value);
    });

    // ==========================================================================
    // LIFECYCLE
    // ==========================================================================

    onMounted(async () => {
      await fetchTargetFields();

      // Initialize selected columns (all selected by default)
      initializeSelectedColumns();

      // Initialize from modelValue if provided, otherwise perform auto-mapping
      if (Object.keys(props.modelValue).length > 0) {
        localMapping.value = { ...props.modelValue };
      } else {
        // Perform initial auto-mapping for contact import
        performInitialAutoMapping();
      }
    });

    return {
      t,
      loading,
      localMapping,
      selectedColumns,
      selectedColumnsCount,
      targetFields,
      isValid,
      validationMessage,
      isPushField,
      pushMappingStatus,
      selectedVapidKeyId,
      vapidKeyOptions,
      loadingVapidKeys,
      getAvailableTargetFields,
      getAvailableFlatTargetFields,
      getFieldLabel,
      isFieldRequired,
      getFieldFormatHint,
      truncateValue,
      onColumnSelectionChange,
    };
  },
});
</script>

<style scoped lang="scss">
.column-mapping-step {
  padding: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 1rem;
  color: var(--text-color-secondary);
}

.mapping-header {
  text-align: center;
  margin-bottom: 2rem;
}

.mapping-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
}

.mapping-subtitle {
  font-size: 0.95rem;
  color: var(--text-color-secondary);
  margin: 0;
}

.validation-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-weight: 500;

  &.is-valid {
    background-color: var(--green-50);
    color: var(--green-700);
    border: 1px solid var(--green-200);

    i {
      color: var(--green-500);
    }
  }

  &.is-invalid {
    background-color: var(--orange-50);
    color: var(--orange-700);
    border: 1px solid var(--orange-200);

    i {
      color: var(--orange-500);
    }
  }
}

.column-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  background-color: var(--primary-100);
  color: var(--primary-700);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 36px;
}

.mapping-table-container {
  overflow-x: auto;
  border: 1px solid var(--surface-200);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--surface-200);
  }

  th {
    background-color: var(--surface-50);
    font-weight: 600;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-color-secondary);

    .th-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      i {
        color: var(--primary-color);
      }
    }
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.mapping-row {
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:hover {
    background-color: var(--surface-50);
  }

  &.is-mapped:not(.is-excluded) {
    background-color: var(--green-50);

    .arrow-icon.is-active {
      color: var(--green-500);
    }
  }

  &.is-excluded {
    opacity: 0.5;
    background-color: var(--surface-100);

    .source-name {
      text-decoration: line-through;
      color: var(--text-color-secondary);
    }

    .preview-value,
    .preview-empty {
      opacity: 0.6;
    }

    &:hover {
      background-color: var(--surface-100);
    }
  }
}

.col-checkbox {
  width: 50px;
  text-align: center;
}

.col-source {
  width: 22%;
  min-width: 150px;
}

.col-arrow {
  width: 50px;
  text-align: center;
}

.col-target {
  width: 35%;
  min-width: 200px;
}

.col-preview {
  width: 25%;
  min-width: 150px;
}

.source-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.source-name {
  font-weight: 500;
  color: var(--text-color);
}

.arrow-icon {
  color: var(--surface-400);
  transition: color 0.2s ease;
}

.target-dropdown {
  width: 100%;
}

.target-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.field-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.email-icon {
  background-color: var(--blue-100);
  color: var(--blue-600);
}

.phone-icon {
  background-color: var(--green-100);
  color: var(--green-600);
}

.push-icon {
  background-color: var(--purple-100);
  color: var(--purple-600);
}

.option-label {
  flex: 1;
}

.option-required {
  color: var(--red-500);
  font-weight: bold;
}

.required-icon {
  font-size: 0.5rem;
  color: var(--red-500);
  margin-left: 0.25rem;
}

.selected-value {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.placeholder-text {
  color: var(--text-color-secondary);
}

.preview-value {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
  font-style: italic;
}

.preview-empty {
  font-size: 0.85rem;
  color: var(--surface-400);
  font-style: italic;
}

.push-warning {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background-color: var(--purple-50);
  border: 1px solid var(--purple-200);
  border-radius: 8px;
  margin-bottom: 1rem;

  > i {
    color: var(--purple-600);
    font-size: 1.25rem;
    margin-top: 0.25rem;
  }

  strong {
    color: var(--purple-800);
  }
}

.target-field-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field-format-hint {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  padding: 0.25rem 0.5rem;
  background-color: var(--surface-100);
  border-radius: 4px;
  margin-top: 0.25rem;

  i {
    color: var(--primary-500);
    font-size: 0.7rem;
  }
}

.consent-icon {
  color: var(--green-500);
}

.option-group-header {
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  background-color: var(--surface-100);
  border-bottom: 1px solid var(--surface-200);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.option-group-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

:deep(.p-dropdown-item-group) {
  padding: 0 !important;
}

.vapid-key-selector {
  border: 1px solid var(--surface-200);
  border-radius: 8px;
  overflow: hidden;
  margin-top: 1.5rem;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background-color: var(--green-50);
    border-bottom: 1px solid var(--green-200);
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--green-800);

    i {
      color: var(--green-600);
    }
  }

  &__body {
    padding: 1rem;
  }

  &__hint {
    margin: 0 0 0.75rem 0;
    font-size: 0.85rem;
    color: var(--text-color-secondary);
  }

  &__dropdown {
    width: 100%;
    max-width: 400px;
  }
}

.vapid-key-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &__icon {
    color: var(--green-500);
    font-size: 0.85rem;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__label {
    font-weight: 500;
  }

  &__badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.5rem;
    background-color: var(--green-100);
    color: var(--green-700);
    border-radius: 10px;
    font-weight: 600;
  }
}

@media (max-width: 768px) {
  .column-mapping-step {
    padding: 1rem;
  }

  .mapping-table th,
  .mapping-table td {
    padding: 0.75rem 0.5rem;
  }

  .col-preview {
    display: none;
  }
}
</style>
